// @flow
import { boolean, number, string } from 'yup';
import React from 'react';
import { FRONT_END_DISPLAY_NAMES } from '@streaming-projects/enums';

import { CHECKBOX_INPUT_TYPE, DATE_INPUT_TYPE, SELECT_INPUT_TYPE } from '../formik-utils/consts';
import {
  DEAL_END_DATE_BACKEND_NAME,
  DEAL_START_DATE_BACKEND_NAME,
  DEAL_TYPE_BACKEND_NAME,
  MARKETPLACE_BACKEND_NAME,
  MARKETPLACE_DISPLAY_NAME,
  PAYMENT_SCHEDULE_BACKEND_NAME,
  PAYMENT_SCHEDULE_DISPLAY_NAME,
  RATE_CARD_BACKEND_NAME,
  RATE_CARD_DISPLAY_NAME,
  SUPPORT_TIER_BACKEND_NAME,
  SUPPORT_TIER_DISPLAY_NAME,
  CC_STARTER_COMMIT_TYPE_BACKEND_NAME,
  CC_STARTER_COMMIT_TYPE_DISPLAY_NAME,
  CC_STARTER_COMMIT_NONE,
  DEAL_TYPE_DISPLAY_NAME,
  DEAL_START_DATE_DISPLAY_NAME,
  DEAL_END_DATE_DISPLAY_NAME,
  CUSTOM_DISCOUNTS_ENABLED_BACKEND_NAME,
  CUSTOM_DISCOUNTS_ENABLED_DISPLAY_NAME,
  CREDITS_AND_BURSTS_ENABLED_DISPLAY_NAME,
  CREDITS_AND_BURSTS_ENABLED_BACKEND_NAME,
  IS_ADVANCED_SECURITY_LIVE_IN_CE1,
  IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1,
  ADVANCED_SECURITY_ENABLED_DISPLAY_NAME,
  ADVANCED_SECURITY_ENABLED_BACKEND_NAME,
  ADVANCED_SECURITY_TOOLTIP_TEXT,
  IS_GATEWAY_ADD_ON_LIVE_IN_CE1,
  GATEWAY_ADD_ON_ENABLED_DISPLAY_NAME,
  GATEWAY_ADD_ON_ENABLED_BACKEND_NAME,
  GATEWAY_ADD_ON_TOOLTIP_TEXT,
  GLOBAL_DISCOUNT_APPLIED_DISPLAY_NAME,
  GLOBAL_DISCOUNT_APPLIED_BACKEND_NAME,
  COMMITTED_CAPACITY_ENABLED_DISPLAY_NAME,
  COMMITTED_CAPACITY_ENABLED_BACKEND_NAME,
} from '../constants';
import { convertLocalToUTCDate, convertUTCToLocalDate } from '../common-utils/utils';
import { StyledExternalLink } from '../components/presentational/ExternalLink';
import { Tooltip } from '../components/presentational/Tooltip';

import { convertConfigArrayToMap } from './utils';

export const discountValidation = number()
  .label('Discount')
  .typeError('You must specify a number')
  .min(0)
  .max(100)
  .required();

export const spGlobalDiscountValidation = number()
  .label('Discount')
  .typeError('You must specify a number')
  .min(0)
  .max(100)
  .required();

export const ESTIMATE_COMMIT_DETAILS_CONFIG = [
  {
    displayName: DEAL_START_DATE_DISPLAY_NAME,
    backendFieldName: DEAL_START_DATE_BACKEND_NAME,
    validation: string().label(DEAL_START_DATE_DISPLAY_NAME).required(),
    inputType: DATE_INPUT_TYPE,
    inputTransformationFunc: convertUTCToLocalDate,
    transformationFunctionToSendToBackEnd: convertLocalToUTCDate,
  },
  {
    displayName: DEAL_END_DATE_DISPLAY_NAME,
    backendFieldName: DEAL_END_DATE_BACKEND_NAME,
    validation: string().label(DEAL_END_DATE_DISPLAY_NAME).required(),
    inputType: DATE_INPUT_TYPE,
    inputTransformationFunc: convertUTCToLocalDate,
    transformationFunctionToSendToBackEnd: convertLocalToUTCDate,
  },
  {
    displayName: DEAL_TYPE_DISPLAY_NAME,
    backendFieldName: DEAL_TYPE_BACKEND_NAME,
    validation: string().label(DEAL_TYPE_DISPLAY_NAME).required(),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: 'New Commit',
  },
  {
    displayName: MARKETPLACE_DISPLAY_NAME,
    backendFieldName: MARKETPLACE_BACKEND_NAME,
    validation: string().label(MARKETPLACE_DISPLAY_NAME).required(),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: 'None',
  },
  {
    displayName: PAYMENT_SCHEDULE_DISPLAY_NAME,
    backendFieldName: PAYMENT_SCHEDULE_BACKEND_NAME,
    validation: string().label(PAYMENT_SCHEDULE_DISPLAY_NAME).required(),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: 'Prepaid',
  },
  {
    displayName: RATE_CARD_DISPLAY_NAME,
    backendFieldName: RATE_CARD_BACKEND_NAME,
    validation: string().label(FRONT_END_DISPLAY_NAMES.RATE_CARD).required(),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: '8/1',
    tooltip: (
      <StyledExternalLink
        href="https://confluentinc.atlassian.net/wiki/spaces/PM/pages/3344084523/Commit+Estimator+Rate+Cards"
        text=" Details"
      />
    ),
  },
  {
    displayName: SUPPORT_TIER_DISPLAY_NAME,
    backendFieldName: SUPPORT_TIER_BACKEND_NAME,
    validation: string().label(SUPPORT_TIER_DISPLAY_NAME).required(),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: 'Free',
  },
  {
    displayName: CC_STARTER_COMMIT_TYPE_DISPLAY_NAME,
    backendFieldName: CC_STARTER_COMMIT_TYPE_BACKEND_NAME,
    // Not .required() - this field doesn't exist on older rate cards and is hidden off of
    // 5/15/2026, so it must never block saving the rest of the Estimate Details form.
    validation: string().label(CC_STARTER_COMMIT_TYPE_DISPLAY_NAME),
    inputType: SELECT_INPUT_TYPE,
    // defaultValue isn't applied to initial values, so cover estimates/backends without the field.
    inputTransformationFunc: (value) => value ?? CC_STARTER_COMMIT_NONE,
    defaultValue: CC_STARTER_COMMIT_NONE,
  },
  {
    displayName: GLOBAL_DISCOUNT_APPLIED_DISPLAY_NAME,
    backendFieldName: GLOBAL_DISCOUNT_APPLIED_BACKEND_NAME,
    validation: discountValidation,
    inputType: SELECT_INPUT_TYPE,
    icon: 'percent',
    inputTransformationFunc: (x) => Number(parseFloat(x).toFixed(8)),
    defaultValue: 0,
  },
  {
    displayName: CUSTOM_DISCOUNTS_ENABLED_DISPLAY_NAME,
    backendFieldName: CUSTOM_DISCOUNTS_ENABLED_BACKEND_NAME,
    validation: boolean().label(CUSTOM_DISCOUNTS_ENABLED_DISPLAY_NAME),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: false,
  },
  {
    displayName: CREDITS_AND_BURSTS_ENABLED_DISPLAY_NAME,
    backendFieldName: CREDITS_AND_BURSTS_ENABLED_BACKEND_NAME,
    validation: boolean().label(CREDITS_AND_BURSTS_ENABLED_DISPLAY_NAME),
    inputType: SELECT_INPUT_TYPE,
    defaultValue: false,
  },
  ...(IS_ECKU_CFU_PACKAGES_LIVE_IN_CE1
    ? [
        {
          displayName: COMMITTED_CAPACITY_ENABLED_DISPLAY_NAME,
          backendFieldName: COMMITTED_CAPACITY_ENABLED_BACKEND_NAME,
          validation: boolean().label(COMMITTED_CAPACITY_ENABLED_DISPLAY_NAME),
          inputType: SELECT_INPUT_TYPE,
          defaultValue: false,
        },
      ]
    : []),
  ...(IS_ADVANCED_SECURITY_LIVE_IN_CE1
    ? [
        {
          displayName: ADVANCED_SECURITY_ENABLED_DISPLAY_NAME,
          backendFieldName: ADVANCED_SECURITY_ENABLED_BACKEND_NAME,
          validation: boolean().label(ADVANCED_SECURITY_ENABLED_DISPLAY_NAME),
          inputType: SELECT_INPUT_TYPE,
          defaultValue: false,
          tooltip: <Tooltip text={ADVANCED_SECURITY_TOOLTIP_TEXT} />,
        },
      ]
    : []),
  ...(IS_GATEWAY_ADD_ON_LIVE_IN_CE1
    ? [
        {
          displayName: GATEWAY_ADD_ON_ENABLED_DISPLAY_NAME,
          backendFieldName: GATEWAY_ADD_ON_ENABLED_BACKEND_NAME,
          validation: boolean().label(GATEWAY_ADD_ON_ENABLED_DISPLAY_NAME),
          inputType: SELECT_INPUT_TYPE,
          defaultValue: false,
          tooltip: <Tooltip text={GATEWAY_ADD_ON_TOOLTIP_TEXT} />,
        },
      ]
    : []),
  {
    displayName: 'New Customer',
    backendFieldName: 'net_new_customer',
    inputType: CHECKBOX_INPUT_TYPE,
    defaultValue: false,
  },
  {
    displayName: 'Is this an MSK takeout?',
    backendFieldName: 'msk_takeout',
    inputType: CHECKBOX_INPUT_TYPE,
    defaultValue: false,
  },
];

export const ESTIMATE_COMMIT_DETAILS_CONFIG_MAP = convertConfigArrayToMap(
  ESTIMATE_COMMIT_DETAILS_CONFIG
);
